/**
 * Facts the detailed flow carries beside its drawing (NS-08, FIX34): per-edge dwell, the edge's
 * share of its source's outflow, the permit record of a permit-based transfer, and what funds a
 * node (its origin). Printed as a table under the figure, not as another figure. Data only: the
 * backend declares the facts, this file words them.
 */
import { esc } from '../../utils/reportCommon';
import { detectChainCandidates } from '../../utils/chainDetect';
import { formatDwell } from '../../services/caseCanvas';
import { NA, hashText, isNum, isRec, present, txt } from './primitives';
import type { FlowDiagramEdge, FlowDiagramIR, FlowDiagramNode, FlowNodeOrigin, PermitRecord } from './types';

const BASIS: Record<string, string> = {
  amount_match: 'matched to the receipt of the same amount',
  fifo: 'first in, first out against earlier receipts',
};
const ORIGIN_CLASS: Record<string, string> = {
  exchange_withdrawal: 'exchange withdrawal',
  own_wallet_cluster: 'own-wallet cluster',
  p2p_counterparty: 'P2P counterparty',
  unknown: 'not classified',
};

const shortRef = (s: unknown, head = 8): string => {
  const v = String(s ?? '');
  return v.length > head + 4 ? `${v.slice(0, head)}…` : v;
};

export function dwellText(e: Pick<FlowDiagramEdge, 'dwell_seconds' | 'dwell_basis'>): string {
  if (!isNum(e.dwell_seconds)) return NA;
  const t = String(formatDwell(e.dwell_seconds) ?? '').replace(/^dwell /, '');
  if (!t) return NA;
  const basis = e.dwell_basis ? (BASIS[e.dwell_basis] || e.dwell_basis) : '';
  return `${esc(t)}${basis ? `<div class="muted">${esc(basis)}</div>` : ''}`;
}

export function shareText(v: unknown): string {
  return isNum(v) ? `${v.toFixed(v % 1 === 0 ? 0 : 1)}%` : NA;
}

/** One line for the permit record: one transfer, its fee leg named. */
export function permitText(p: PermitRecord | Record<string, unknown> | null | undefined): string {
  if (!isRec(p)) return '';
  const r = p as PermitRecord;
  const scheme = r.scheme && r.scheme !== 'permit_transfer' ? `${r.scheme} permit` : 'permit';
  const fee = isNum(r.fee_value) ? `${r.fee_value} ${r.fee_token ?? ''}`.trim() : 'fee amount not stated';
  const to = r.fee_recipient_label || (r.fee_recipient ? String(r.fee_recipient) : 'fee recipient not stated');
  const same = r.fee_tx && r.fee_tx === r.tx_hash ? 'same transaction' : `fee tx ${shortRef(r.fee_tx)}`;
  return `${scheme}: fee ${fee} to ${to} (${same})`;
}

export function originText(o: FlowNodeOrigin | null | undefined): string {
  if (!isRec(o)) return '';
  const cls = ORIGIN_CLASS[String(o.class)] || String(o.class || 'not classified');
  const who = o.entity || o.address || 'origin address not stated';
  const share = isNum(o.share_of_feeder_inflow) ? `, ${Math.round(o.share_of_feeder_inflow * 100)}% of its inflow` : '';
  return `${cls}: ${who}${share}`;
}

/** The short form a label prints for a wallet (first characters, ellipsis, last characters). */
const SHORT_FORM = /^\S{3,8}(?:\.\.\.|…)\S{3,6}$/;

/**
 * The label lines of a box with the wallet's full address in place of its short form. A box whose label
 * prints no short form keeps its lines and gets the address after them; a box without an address keeps
 * its lines. The short form alone is ambiguous (two wallets can share it), so every text that names a
 * wallet uses this (FIG-01).
 */
export function nodeLineList(n: FlowDiagramNode | undefined): string[] {
  if (!n) return [];
  const raw = (n.label_lines || []).filter((l) => l);
  const addr = n.address ? String(n.address) : '';
  if (!addr) return raw;
  if (raw.some((l) => SHORT_FORM.test(l.trim()))) return raw.map((l) => (SHORT_FORM.test(l.trim()) ? addr : l));
  return raw.includes(addr) ? raw : [...raw, addr];
}

const lines = (n: FlowDiagramNode | undefined): string => nodeLineList(n).join(' ');

const ROLE_TEXT: Record<string, string> = {
  subject: 'subject', source: 'source', sink: 'sink', relay: 'relay', shared_relay: 'shared relay', context: 'context',
};

/** The chain a wallet address belongs to by its format, or the honest reason it is not one chain. */
export function chainOfAddress(address: string): string {
  const c = detectChainCandidates(address);
  if (c.length === 1) return c[0];
  if (c.length > 1) return 'EVM (the address does not fix the chain)';
  return 'not recognised';
}

function roleOf(n: FlowDiagramNode): string {
  const r = String(n.placement_role || '').trim();
  if (n.read_as === 'value_recipient_contract') return `${ROLE_TEXT[r] || r || 'relay'} (contract that received the case's value)`;
  if (r) return ROLE_TEXT[r] || r.replace(/_/g, ' ');
  const c = String(n.entity_class || '').trim();
  return c ? c.replace(/_/g, ' ') : 'not stated';
}

/**
 * The wallet legend as the page shows it: collapsed on screen (a details element, hidden in print); in print always
 * the table itself, whatever the figure's size, so a printout never depends on another appendix being included
 * (the boxes print a short form, and two wallets can share one). Rows are not kept together, so a long legend
 * breaks across pages.
 */
export function figureWalletBlock(ir: FlowDiagramIR, id: string): string {
  const table = figureWalletTable(ir, id);
  if (!table) return '';
  const wallets = (ir.nodes || []).filter((x) => x.address || x.recipient_address).length;
  const print = `<div class="print-only wallet-legend-print">${figureWalletTable(ir, id, `figure-wallets-print-${id}`)}</div>`;
  return `<details class="wallet-details screen-only" data-testid="figure-wallets-details-${esc(id)}"><summary>Wallets in this figure, in full (${wallets})</summary>${table}</details>${print}`;
}

/** Every wallet box of a figure: short label, full address, chain, role. Empty when no box carries an address. */
export function figureWalletTable(ir: FlowDiagramIR, id: string, testid = `figure-wallets-${id}`): string {
  const rows = (ir.nodes || []).filter((n) => n.address || n.recipient_address);
  if (!rows.length) return '';
  const body = rows.map((n) => {
    const name = (n.label_lines || []).filter((l) => l && !SHORT_FORM.test(l.trim())).join(' ');
    const short = (n.label_lines || []).find((l) => SHORT_FORM.test((l || '').trim()));
    const cells = [n.address ? hashText(n.address) : '', n.recipient_address ? `${hashText(n.recipient_address)} <span class="muted">(notice recipient)</span>` : '']
      .filter(Boolean).join('<br>');
    const chain = chainOfAddress(String(n.address || n.recipient_address));
    return `<tr><td>${txt(name || n.id)}${short ? `<div class="muted">${esc(short.trim())}</div>` : ''}</td><td>${cells}</td><td>${esc(chain)}</td><td>${esc(roleOf(n))}</td></tr>`;
  }).join('');
  return `<table class="data wallet-legend" data-testid="${esc(testid)}"><caption class="muted">Wallets in this figure, in full (the boxes print a short form, and two different wallets can share one).</caption><thead><tr><th>Box</th><th>Address</th><th>Chain</th><th>Role</th></tr></thead><tbody>${body}</tbody></table>`;
}

/** The note under an amount that is the sum of several transfers (the hash printed is the latest of them). */
export function moreTransfers(e: Pick<FlowDiagramEdge, 'transfer_count'>): string {
  const n = e.transfer_count;
  return isNum(n) && n > 1
    ? `<div class="muted">+${n - 1} more transfer${n - 1 === 1 ? '' : 's'} (${n} in all; this amount is their sum under one hash)</div>` : '';
}

export function hasFlowFacts(ir: FlowDiagramIR | null | undefined): boolean {
  if (!isRec(ir)) return false;
  if ((ir.edges || []).some((e) => present(e.share_note))) return true;
  if (ir.facts_joined !== true) return false;
  return (ir.edges || []).some((e) => isNum(e.dwell_seconds) || isNum(e.share_pct) || isRec(e.permit))
    || (ir.nodes || []).some((n) => isRec(n.origin));
}

/** Plain-language legend for the facts that appear in this diagram. */
export function flowFactsLegend(ir: FlowDiagramIR): string {
  const out: string[] = [];
  if ((ir.edges || []).some((e) => present(e.share_note))) out.push("A label of the form 'X of Y' is the case's part of a spend that paid Y: X is the part that traces to the case, and only X is counted. '(upper bound)' means the spend's output count was not known and was read as one output.");
  if ((ir.edges || []).some((e) => isNum(e.dwell_seconds))) out.push('Dwell is how long the sender held the funds between the receipt that funded the transfer and the transfer itself.');
  if ((ir.edges || []).some((e) => isNum(e.share_pct))) out.push('Share is the transfer as a percentage of everything the sender sent in the same token in this flow; amounts of different tokens are never added.');
  if ((ir.edges || []).some((e) => isRec(e.permit))) out.push('A permit record means the fee for the transfer was paid as a second token movement in the same transaction; the two movements are one transfer here, the fee shown in the record.');
  if ((ir.nodes || []).some((n) => isRec(n.origin))) out.push('Origin is who paid the feeder before the case, from the upstream trace; a payment after the case effect is never shown as an origin.');
  return esc(out.join(' '));
}

/** Table of the facts under the detailed figure; empty text when the data carries none. */
export function flowFactsTable(ir: FlowDiagramIR): string {
  if (!hasFlowFacts(ir)) return '';
  const byId = new Map((ir.nodes || []).map((n) => [n.id, n] as const));
  const rows = (ir.edges || []).map((e, i) => `<tr><td>${i + 1}</td><td>${txt(lines(byId.get(e.source)))}</td>
    <td>${txt(lines(byId.get(e.target)))}</td><td>${txt((e.label_lines || [])[0] ?? '')}${moreTransfers(e)}${present(e.share_note) ? `<div class="muted" data-testid="flow-share-note">${esc(String(e.share_note))}${e.share_upper_bound ? ' (upper bound)' : ''}</div>` : ''}</td>
    <td>${dwellText(e)}</td><td>${shareText(e.share_pct)}</td>
    <td>${isRec(e.permit) ? `${esc(permitText(e.permit))}${present(e.permit.tx_hash) ? `<div>${hashText(e.permit.tx_hash)}</div>` : ''}` : NA}</td></tr>`).join('');
  const origins = (ir.nodes || []).filter((n) => isRec(n.origin));
  const originRows = origins.map((n) => `<tr><td>${txt(lines(n))}</td><td>${esc(originText(n.origin))}</td>
    <td>${hashText((n.origin as FlowNodeOrigin).evidence_ref?.tx_hash)}</td></tr>`).join('');
  return `<div data-testid="flow-facts" class="wide-page"><h4>Dwell, share and permit records of the detailed flow</h4>
    <table class="data" data-testid="flow-facts-edges"><thead><tr><th>#</th><th>From</th><th>To</th><th>Amount</th><th>Dwell at sender</th><th>Share of sender outflow</th><th>Permit record</th></tr></thead><tbody>${rows}</tbody></table>
    ${origins.length ? `<h4>Origin of the feeders</h4><table class="data" data-testid="flow-facts-origins"><thead><tr><th>Wallet</th><th>Origin</th><th>Evidence transfer</th></tr></thead><tbody>${originRows}</tbody></table>` : ''}
    <p class="muted" data-testid="flow-facts-legend">${flowFactsLegend(ir)}</p></div>`;
}
